import { Controller } from '@hotwired/stimulus';
import { trapFocus, visibleFocusables } from '../utils/focus_trap.js';
import { otherDialogOpen } from './sidebar_controller.js';

/**
 * Palette de recherche globale (⌘K / Ctrl K) : back-office (`admin.html.erb`) et agence
 * (`application.html.erb`). UNE instance, posée sur `<body>` ; le dialogue est rendu par
 * `Ui::CommandPaletteComponent` avec `controller_scope: false` (les valeurs vivent sur `<body>`).
 *
 * Targets:
 * - dialog : la racine du dialogue (`hidden` quand fermé, `data-turbo-prefetch="false"`)
 * - input  : le champ, `role="combobox"`, `aria-controls="command-palette-listbox"`
 * - results : le conteneur défilant qui tient `<turbo-frame id="command-palette-results">`
 * - result : chaque option (`<a role="option" id tabindex="-1" data-turbo-frame="_top">`),
 *   parcourue dans l'ordre du DOM par ↑↓ ; plusieurs options peuvent partager une ligne
 *
 * Values:
 * - searchUrl : l'endpoint qui répond le frame (`/admin/search`, `/recherche`)
 * - recentKey : clé `localStorage` des récents du back-office
 * - emptyText : texte de la fenêtre vide quand les récents sont locaux (back-office)
 * - serverRecents : `true` ⇒ requête vide = le serveur rend les récents (`?q=`), rien en local
 * - modifier : 'meta' (⌘ seul, Mac), 'ctrl' (Ctrl seul), '' (l'un ou l'autre, ancien comportement)
 *
 * Actions:
 * - open / close / closeOnOverlay
 * - search (input), handleKeydown (keydown du champ)
 * - hover (mouseenter d'une option), selectResult (clic d'une option)
 * - useExample (bouton d'exemple d'une réponse, param `query`)
 * - frameLoaded (`turbo:frame-load` du frame des résultats)
 * - frameMissing (`turbo:frame-missing` du frame : réponse redirigée, session expirée)
 * - trackRequest (`turbo:before-fetch-request` du frame : garde de quoi annuler sa requête quand la saisie
 *   s'efface au back-office ou que la palette se ferme ; une requête venue d'un descendant n'est jamais touchée)
 * - keepFocus (`click` du panneau : un clic hors d'un contrôle, sans sélection de texte, rend le focus au champ)
 *
 * @example
 * <body data-controller="command-palette"
 *       data-command-palette-search-url-value="/recherche"
 *       data-command-palette-server-recents-value="true"
 *       data-command-palette-modifier-value="meta">
 *   <button data-action="click->command-palette#open">Rechercher</button>
 *   <%= render Ui::CommandPaletteComponent.new(search_url: search_path, controller_scope: false) %>
 * </body>
 */

const DEFAULT_EMPTY_TEXT = 'Tapez pour rechercher des organisations, utilisateurs, chantiers...';
const LISTBOX_ID = 'command-palette-listbox';
const RECENT_HEADING_ID = 'command-palette-recent-heading';
const SIDEBAR_SELECTOR = '[data-sidebar-target="sidebar"]';
const SCROLL_LOCK_CLASS = 'overflow-hidden';
// Ce qui garde son clic : le champ, une option, un raccourci, le bouton d'exemple.
const CONTROL_SELECTOR = 'input, a, button';

/**
 * Le raccourci ⌘K / Ctrl K correspond-il à la plateforme ?
 *
 * Sur Mac, Ctrl+K efface la fin de ligne dans tout champ (CR compris) : `meta` n'accepte que ⌘.
 * Une frappe en cours de composition (IME, accent mort) n'est jamais un raccourci.
 *
 * @param {KeyboardEvent} event
 * @param {'meta'|'ctrl'|''} modifier
 * @returns {boolean}
 */
export function shortcutMatches(event, modifier) {
  if (event.isComposing) return false;
  if (String(event.key).toLowerCase() !== 'k') return false;
  if (modifier === 'meta') return Boolean(event.metaKey && !event.ctrlKey);
  if (modifier === 'ctrl') return Boolean(event.ctrlKey && !event.metaKey);
  return Boolean(event.metaKey || event.ctrlKey);
}

/**
 * La réponse chargée dans le frame est-elle celle d'une requête périmée ?
 *
 * Le frame garde en `src` l'URL de la réponse qu'il vient de rendre (Turbo la repose au chargement).
 * Si son `q` ne vaut plus la saisie courante, l'utilisateur a tapé depuis : Entrée ne doit pas
 * ouvrir la première ligne de l'ANCIENNE requête. Sans `src` (frame vidé), rien n'est à jour.
 *
 * @param {string|null} src
 * @param {string} currentQuery
 * @returns {boolean}
 */
export function isStaleLoad(src, currentQuery) {
  if (!src) return true;
  let loaded;
  try {
    loaded = new URL(src, 'http://palette.invalid').searchParams.get('q') ?? '';
  } catch {
    return true;
  }
  return loaded.trim() !== String(currentQuery ?? '').trim();
}

/**
 * Le raccourci doit-il rester sans effet parce qu'un autre dialogue est affiché ?
 *
 * Même garde que l'Échap du menu (`otherDialogOpen`, `sidebar_controller.js`) : une modale ou un
 * slide-over ouverts gardent la main. Le menu lui-même (feuille « Menu » du téléphone,
 * superposition tablette, `<aside role="dialog">`) ne bloque pas (D9).
 *
 * @param {Document} [root]
 * @returns {boolean}
 */
export function shortcutBlockedByDialog(root = document) {
  return otherDialogOpen(root.querySelector(SIDEBAR_SELECTOR), root);
}

/**
 * Une option peut-elle devenir active ? Les raccourcis d'un chantier sont masqués sous `sm`
 * (`display: none`) mais restent des cibles `result` : ↑↓ s'y arrêterait, sans surbrillance, et
 * Entrée ouvrirait une destination invisible. `opacity-0` (raccourcis au repos) compte comme visible.
 *
 * @param {Element} el
 * @returns {boolean}
 */
export function isNavigable(el) {
  if (typeof el.checkVisibility === 'function') return el.checkVisibility();
  return el.getClientRects().length > 0;
}

export default class extends Controller {
  static values = {
    searchUrl: String,
    recentKey: { type: String, default: 'jerico_cmd_palette_recent' },
    emptyText: { type: String, default: DEFAULT_EMPTY_TEXT },
    serverRecents: { type: Boolean, default: false },
    modifier: { type: String, default: '' },
  };

  static targets = ['dialog', 'input', 'results', 'result'];

  connect() {
    this.boundHandleKeydown = this.handleGlobalKeydown.bind(this);
    this.boundHandleTab = this.handleTabKey.bind(this);
    this.boundBeforeCache = this.handleBeforeCache.bind(this);
    document.addEventListener('keydown', this.boundHandleKeydown);
    document.addEventListener('turbo:before-cache', this.boundBeforeCache);
    this.debounceTimer = null;
    this.activeIndex = -1;
    this.pendingQuery = false;
    this.lockedScroll = false;
    this.previouslyFocused = null;
    this.frameRequest = null;
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundHandleKeydown);
    document.removeEventListener('turbo:before-cache', this.boundBeforeCache);
    this.cancelFrameRequest();
    this.clearDebounce();
    this.removeFocusTrap();
    this.releaseScroll();
  }

  handleGlobalKeydown(event) {
    if (shortcutMatches(event, this.modifierValue)) {
      // Toujours annulé : bloqué par une modale, le raccourci ne doit pas non plus donner la main
      // au navigateur (Ctrl+K = barre de recherche de Chrome / Firefox) et sortir le focus de la modale.
      event.preventDefault();
      if (this.isOpen()) {
        this.close();
      } else if (!shortcutBlockedByDialog(document)) {
        this.closeMenuDialog();
        this.open();
      }
      return;
    }

    if (event.key === 'Escape' && this.isOpen()) {
      event.preventDefault();
      this.close();
    }
  }

  // Turbo clone la page qu'il quitte : une palette encore ouverte reviendrait ouverte au
  // « Précédent », `body` bloqué (précédent `modal_controller.js#closeBeforeCache`). Pas de retour
  // de focus : la page s'en va, ce n'est pas un geste de l'utilisateur.
  handleBeforeCache() {
    if (this.isOpen()) this.close({ restoreFocus: false });
  }

  isOpen() {
    return this.hasDialogTarget && !this.dialogTarget.classList.contains('hidden');
  }

  open() {
    if (this.isOpen()) return;

    const active = document.activeElement;
    this.previouslyFocused =
      active && active !== document.body && !this.dialogTarget.contains(active) ? active : null;

    this.dialogTarget.classList.remove('hidden');
    // La feuille « Menu » fermée dans le même geste (bouton du menu, ⌘K) rend le défilement APRÈS ce code :
    // `hideSidebar` est un rappel de valeur Stimulus, livré en microtâche. Le verrou se décide après lui,
    // sinon la palette croirait le `<body>` déjà bloqué et la page défilerait derrière elle.
    queueMicrotask(() => {
      if (this.isOpen()) this.lockScroll();
    });

    // Focus SYNCHRONE, dans le geste de l'utilisateur : iOS n'ouvre le clavier que là (R27).
    this.inputTarget.value = '';
    this.inputTarget.setAttribute('aria-expanded', 'true');
    this.inputTarget.focus();
    this.installFocusTrap();
    this.clearDebounce();
    this.resetActive();

    this.showEmptyState();
  }

  lockScroll() {
    if (this.lockedScroll || document.body.classList.contains(SCROLL_LOCK_CLASS)) return;
    document.body.classList.add(SCROLL_LOCK_CLASS);
    this.lockedScroll = true;
  }

  // ⌘K pendant que le menu est un dialogue (feuille « Menu » du téléphone, superposition de la tablette) :
  // le fermer d'abord, comme le bouton du menu (`sidebar#close sidebar-collapse#close`), sinon la palette
  // s'ouvre sous un `aria-modal` que les lecteurs d'écran ne quittent pas.
  closeMenuDialog() {
    const aside = document.querySelector(SIDEBAR_SELECTOR);
    if (aside?.getAttribute('aria-modal') !== 'true') return;

    const drawer = aside.closest('[data-controller~="sidebar"]');
    if (drawer) this.application.getControllerForElementAndIdentifier(drawer, 'sidebar')?.close();
    this.application.getControllerForElementAndIdentifier(aside, 'sidebar-collapse')?.close();
  }

  close({ restoreFocus = true } = {}) {
    this.dialogTarget.classList.add('hidden');
    this.releaseScroll();
    this.clearDebounce();
    this.pendingQuery = false;
    this.inputTarget.value = '';
    this.inputTarget.setAttribute('aria-expanded', 'false');
    this.resetActive();
    this.removeFocusTrap();
    this.clearFrame();

    const opener = this.previouslyFocused;
    this.previouslyFocused = null;
    if (restoreFocus && opener?.isConnected && typeof opener.focus === 'function') {
      opener.focus({ preventScroll: true });
    }
  }

  closeOnOverlay(event) {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.navigateDown();
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.navigateUp();
      return;
    }

    // Entrée qui valide une composition (IME, accent mort) n'ouvre rien.
    if (event.key === 'Enter' && !event.isComposing) {
      event.preventDefault();
      this.navigateToActive();
    }
  }

  // ── Focus Trapping ──
  //
  // The listener is scoped to `dialogTarget`, NOT to `document` — deliberate: the trap must only
  // exist while the palette is open, and the palette is a long-lived element that stays connected
  // (hidden) the rest of the time. install/remove IS the gate here. `removeFocusTrap()` is called
  // from both `close()` and `disconnect()`, so the connect/disconnect symmetry holds.

  installFocusTrap() {
    this.dialogTarget.removeEventListener('keydown', this.boundHandleTab);
    this.dialogTarget.addEventListener('keydown', this.boundHandleTab);
  }

  removeFocusTrap() {
    if (!this.hasDialogTarget) return;
    this.dialogTarget.removeEventListener('keydown', this.boundHandleTab);
  }

  // Les options sont en `tabindex="-1"` (le focus reste dans le champ, `aria-activedescendant`
  // désigne l'option active) mais `a[href]` les garde dans la liste de `visibleFocusables` : le piège
  // partagé croirait qu'une option est le dernier arrêt et laisserait le Tab natif quitter le
  // dialogue depuis le champ. Le cycle est donc calculé sur les seuls arrêts de Tab (champ, bouton
  // d'exemple, « Fermer » au doigt) ; sans aucun, le piège partagé garde son repli (focus sur le dialogue).
  // Le Tab natif n'est JAMAIS laissé passer, même depuis un arrêt du milieu : il suit l'ordre du
  // document, donc peut atterrir sur la liste exclue du cycle (QA-48 : Maj+Tab depuis l'exemple entrait
  // dans la liste, le suivant renvoyait au dernier arrêt, et le champ n'était plus jamais atteint).
  handleTabKey(event) {
    if (event.key !== 'Tab') return;

    // La liste qui défile est focalisable pour le clavier (axe scrollable-region-focusable) mais n'est pas un
    // arrêt de Tab : ses lignes se parcourent par ↑↓ depuis le champ, qui doit garder le focus.
    const stops = visibleFocusables(this.dialogTarget).filter(
      (el) =>
        el.getAttribute('tabindex') !== '-1' &&
        !(this.hasResultsTarget && el === this.resultsTarget)
    );
    if (!stops.length) {
      trapFocus(this.dialogTarget, event);
      return;
    }

    event.preventDefault();
    const count = stops.length;
    // Hors du cycle (index -1) : Tab va au premier arrêt, Maj+Tab au dernier.
    const index = stops.indexOf(document.activeElement);
    if (event.shiftKey) {
      stops[index <= 0 ? count - 1 : index - 1].focus();
    } else {
      stops[(index + 1) % count].focus();
    }
  }

  // ── Search ──

  search() {
    this.clearDebounce();
    this.resetActive();
    this.pendingQuery = true;

    const query = this.inputTarget.value.trim();
    if (!query) {
      this.showEmptyState();
      return;
    }

    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.loadFrame(query);
    }, 300);
  }

  useExample({ params }) {
    const query = String(params?.query ?? '');
    this.inputTarget.value = query;
    this.inputTarget.focus();
    this.clearDebounce();
    this.resetActive();
    this.pendingQuery = true;
    if (query.trim()) {
      this.loadFrame(query.trim());
    } else {
      this.showEmptyState();
    }
  }

  // UN seul endroit construit l'URL de recherche : les tests d'intégration de l'endpoint
  // (`test/integration/search_test.rb`) citent cette ligne et rejouent son `?q=`.
  searchUrlFor(query) {
    return `${this.searchUrlValue}?q=${encodeURIComponent(query)}`;
  }

  loadFrame(query) {
    const frame = this.frame();
    if (!frame) return;
    frame.setAttribute('src', this.searchUrlFor(query));
  }

  // Fenêtre vide (ouverture, saisie effacée) : récents du serveur ou récents locaux du back-office.
  showEmptyState() {
    if (this.serverRecentsValue) {
      this.pendingQuery = true;
      this.loadFrame('');
      return;
    }

    // Aucune requête ne remplace celle en vol : on l'annule, sinon l'indicateur tourne et les récents restent
    // grisés jusqu'à sa réponse périmée (QA-38). Une réponse déjà reçue reste jugée périmée par `frameLoaded`.
    this.cancelFrameRequest();
    this.pendingQuery = false;
    const frame = this.frame();
    if (frame) frame.removeAttribute('src');
    this.renderRecent();
  }

  frameLoaded(event) {
    const frame = this.frame();
    if (!frame || (event?.target && event.target !== frame)) return;

    if (!this.isOpen()) {
      this.clearFrame();
      return;
    }

    const query = this.inputTarget.value.trim();
    if (isStaleLoad(frame.getAttribute('src'), query)) {
      // Une réponse arrivée après l'effacement de la saisie remplace les récents locaux : on les remet.
      if (!query && !this.serverRecentsValue) {
        frame.removeAttribute('src');
        this.renderRecent();
      }
      return;
    }
    if (this.debounceTimer) return;

    this.pendingQuery = false;
    this.activeIndex = this.navigableResults().length ? 0 : -1;
    this.updateActiveResult({ scroll: false });
  }

  // Un clic dans la fenêtre hors d'un contrôle (en-tête de groupe, ligne inerte du cas mixte, pied, liste)
  // laisse le focus ailleurs que dans le champ : ↑↓ et Entrée, qui vivent sur le champ, ne répondraient plus.
  // Au `click` et sans rien annuler : sélectionner du texte (un montant) et faire glisser la barre de défilement
  // restent possibles, et une sélection en cours n'est pas perdue.
  keepFocus(event) {
    if (event.target?.closest?.(CONTROL_SELECTOR)) return;
    if (window.getSelection?.()?.isCollapsed === false) return;
    this.inputTarget.focus({ preventScroll: true });
  }

  // Réponse sans le frame (session expirée ou fermée dans un autre onglet : 302 vers la connexion) : au lieu
  // du « Content missing » de Turbo dans la palette, la page de la réponse s'ouvre en entier.
  frameMissing(event) {
    const frame = this.frame();
    if (!frame || event.target !== frame) return;

    event.preventDefault();
    this.close({ restoreFocus: false });
    event.detail.visit(event.detail.response);
  }

  navigateDown() {
    const results = this.navigableResults();
    if (!results.length) return;

    this.activeIndex = Math.min(this.activeIndex + 1, results.length - 1);
    this.updateActiveResult();
  }

  navigateUp() {
    const results = this.navigableResults();
    if (!results.length) return;

    this.activeIndex = Math.max(this.activeIndex - 1, 0);
    this.updateActiveResult();
  }

  hover(event) {
    const index = this.navigableResults().indexOf(event.currentTarget);
    if (index === -1 || index === this.activeIndex) return;
    this.activeIndex = index;
    this.updateActiveResult({ scroll: false });
  }

  resetActive() {
    this.activeIndex = -1;
    this.updateActiveResult({ scroll: false });
  }

  // Options que ↑↓, le survol et Entrée parcourent : `activeIndex` est un rang dans CETTE liste.
  navigableResults() {
    return this.resultTargets.filter(isNavigable);
  }

  updateActiveResult({ scroll = true } = {}) {
    let activeId = null;
    const active = this.navigableResults()[this.activeIndex];
    this.resultTargets.forEach((el) => {
      if (el === active) {
        el.setAttribute('data-active', '');
        el.setAttribute('aria-selected', 'true');
        activeId = el.id || null;
        if (scroll) el.scrollIntoView({ block: 'nearest' });
      } else {
        el.removeAttribute('data-active');
        el.setAttribute('aria-selected', 'false');
      }
    });

    if (!this.hasInputTarget) return;
    if (activeId) {
      this.inputTarget.setAttribute('aria-activedescendant', activeId);
    } else {
      this.inputTarget.removeAttribute('aria-activedescendant');
    }
  }

  navigateToActive() {
    // Une requête attend encore son délai ou sa réponse : la ligne active est celle d'AVANT (R6).
    if (this.pendingQuery || this.debounceTimer) return;

    const results = this.navigableResults();
    if (this.activeIndex < 0 || this.activeIndex >= results.length) return;

    const active = results[this.activeIndex];
    const href = active.getAttribute('href');
    if (!href) return;

    this.saveRecent(active.dataset.resultLabel || '', href);
    window.Turbo.visit(href);
    this.close({ restoreFocus: false });
  }

  selectResult(event) {
    // ⌘ / Ctrl / Maj-clic, clic du milieu : le lien s'ouvre ailleurs, la palette reste ouverte avec sa saisie.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;

    const target = event.currentTarget;
    const label = target.dataset.resultLabel || '';
    const href = target.getAttribute('href');
    if (label && href) {
      this.saveRecent(label, href);
    }
    this.close({ restoreFocus: false });
  }

  // ── Frame ──

  frame() {
    if (!this.hasResultsTarget) return null;
    return this.resultsTarget.querySelector('turbo-frame');
  }

  // Turbo n'offre aucune annulation publique d'une requête de frame. Avant son `fetch`, il passe ses
  // `fetchOptions` à `turbo:before-fetch-request` : le signal y est remplacé par celui de la palette, lié à celui
  // de Turbo pour que son annulation à lui (nouveau `src`) atteigne toujours le `fetch`. Un abandon finit en
  // `AbortError`, que Turbo tait : rien n'est rendu et son `finally` retire `aria-busy`, donc l'indicateur.
  // L'événement remonte aussi des descendants (lien `_top` d'une ligne) : seule la requête du frame est suivie.
  trackRequest(event) {
    const frame = this.frame();
    const fetchOptions = event?.detail?.fetchOptions;
    if (!frame || event.target !== frame || !fetchOptions) return;

    const request = new AbortController();
    const turboSignal = fetchOptions.signal;
    if (turboSignal?.aborted) request.abort();
    turboSignal?.addEventListener('abort', () => request.abort(), { once: true });
    fetchOptions.signal = request.signal;
    this.frameRequest = request;
  }

  cancelFrameRequest() {
    this.frameRequest?.abort();
    this.frameRequest = null;
  }

  // Palette fermée : la requête en vol est annulée AVANT de vider le frame, sinon `aria-busy` reste posé et
  // l'indicateur réapparaît à la réouverture jusqu'à sa réponse (QA-32).
  clearFrame() {
    this.cancelFrameRequest();
    const frame = this.frame();
    if (!frame) return;
    frame.removeAttribute('src');
    while (frame.firstChild) frame.removeChild(frame.firstChild);
  }

  clearDebounce() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = null;
  }

  releaseScroll() {
    if (!this.lockedScroll) return;
    document.body.classList.remove(SCROLL_LOCK_CLASS);
    this.lockedScroll = false;
  }

  // ── Récents locaux (back-office seulement : `serverRecents` false) ──

  renderRecent() {
    const recent = this.getRecent();
    const frame = this.frame();
    if (!frame) return;

    // Clear existing content safely
    while (frame.firstChild) frame.removeChild(frame.firstChild);

    const list = document.createElement('div');
    list.setAttribute('role', 'listbox');
    list.id = LISTBOX_ID;
    list.setAttribute('aria-label', 'Résultats');
    frame.appendChild(list);

    const entries = recent.filter((r) => r && typeof r.url === 'string' && r.url);
    if (!entries.length) {
      const empty = document.createElement('div');
      empty.className = 'px-4 py-8 text-center text-sm text-neutral-500';
      empty.textContent = this.emptyTextValue;
      frame.appendChild(empty);
      return;
    }

    const group = document.createElement('div');
    group.setAttribute('role', 'group');
    group.setAttribute('aria-labelledby', RECENT_HEADING_ID);

    const header = document.createElement('div');
    header.id = RECENT_HEADING_ID;
    header.className = 'px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wider';
    header.textContent = 'Récents';
    group.appendChild(header);

    entries.forEach((r, index) => {
      const labelText = String(r.label ?? '');
      const link = document.createElement('a');
      link.href = r.url;
      link.id = `command-palette-recent-${index}`;
      link.setAttribute('data-command-palette-target', 'result');
      link.setAttribute(
        'data-action',
        'mouseenter->command-palette#hover click->command-palette#selectResult'
      );
      link.setAttribute('data-turbo-frame', '_top');
      link.setAttribute('data-result-label', labelText);
      link.setAttribute('role', 'option');
      link.setAttribute('tabindex', '-1');
      link.setAttribute('aria-selected', 'false');
      link.className =
        'flex items-center gap-3 px-4 py-2.5 hover:bg-primary-50 cursor-pointer transition-colors data-[active]:bg-primary-50';

      // Clock icon (SVG)
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'h-4 w-4 text-neutral-400 shrink-0');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke', 'currentColor');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('aria-hidden', 'true');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('stroke-linecap', 'round');
      path.setAttribute('stroke-linejoin', 'round');
      path.setAttribute('stroke-width', '2');
      path.setAttribute('d', 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z');
      svg.appendChild(path);
      link.appendChild(svg);

      const label = document.createElement('span');
      label.className = 'text-sm text-neutral-700 truncate';
      label.textContent = labelText;
      link.appendChild(label);

      group.appendChild(link);
    });

    list.appendChild(group);
  }

  saveRecent(label, url) {
    // Côté agence les récents sont ceux du serveur (session, par agence) : rien en local.
    if (this.serverRecentsValue) return;

    const recent = this.getRecent();
    const entry = { label, url, timestamp: Date.now() };
    const filtered = recent.filter((r) => r.url !== url);
    filtered.unshift(entry);
    try {
      localStorage.setItem(this.recentKeyValue, JSON.stringify(filtered.slice(0, 5)));
    } catch {
      // localStorage unavailable
    }
  }

  getRecent() {
    try {
      const parsed = JSON.parse(localStorage.getItem(this.recentKeyValue) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
}
